import {
  Facebook,
  Instagram,
  Mail,
  MapPin,
  Menu,
  Phone,
  ShoppingBag,
  Youtube,
  X,
} from 'lucide-react'
import { useEffect, useRef, useState, type KeyboardEvent } from 'react'
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom'
import { contact, navItems, socialLinks } from '../data/site'
import Brand from './Brand'
import DonateButton from './DonateButton'
import PrivacyPreferences, { openPrivacySettings } from './PrivacyPreferences'
import SiteMetadata from './SiteMetadata'
import SiteUpdateNotice from './SiteUpdateNotice'

export default function Layout() {
  const [open, setOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const location = useLocation()
  const menuButton = useRef<HTMLButtonElement>(null)
  const closeButton = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    setOpen(false)
    let firstFrame = 0
    let secondFrame = 0

    if (location.hash) {
      firstFrame = window.requestAnimationFrame(() => {
        secondFrame = window.requestAnimationFrame(() => {
          const target = document.getElementById(decodeURIComponent(location.hash.slice(1)))
          const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
          target?.scrollIntoView({
            behavior: reduceMotion ? 'auto' : 'smooth',
            block: 'start',
          })
        })
      })
    } else {
      window.scrollTo({ top: 0, behavior: 'auto' })
    }

    return () => {
      window.cancelAnimationFrame(firstFrame)
      window.cancelAnimationFrame(secondFrame)
    }
  }, [location.hash, location.key, location.pathname])

  useEffect(() => {
    if (!open) return
    closeButton.current?.focus()
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
      menuButton.current?.focus()
    }
  }, [open])

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  const trapDrawerFocus = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key !== 'Tab') return
    const focusable = Array.from(event.currentTarget.querySelectorAll<HTMLElement>('a[href], button:not([disabled])'))
    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last?.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first?.focus()
    }
  }

  return (
    <div className="site-shell">
      <SiteMetadata />
      <a className="skip-link" href="#main-content">Skip to content</a>
      <div className="capacity-bar">
        <div className="container capacity-bar__inner">
          <strong>Sanctuary update:</strong>
          <span>Currently at full capacity. Visits and groups are limited to approved requests.</span>
        </div>
      </div>

      <header className={`site-header ${scrolled ? 'is-scrolled' : ''}`}>
        <div className="container site-header__inner">
          <NavLink to="/" aria-label="Unique Diamond Horse Sanctuary home" className="site-header__brand">
            <Brand compact={scrolled} />
          </NavLink>

          <nav className="desktop-nav" aria-label="Primary navigation">
            {navItems.map((item) => (
              <NavLink key={item.to} to={item.to} end={item.to === '/'}>
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="site-header__actions">
            <Link className="icon-button icon-button--shop" to="/shop" aria-label="Visit the sanctuary shop page">
              <ShoppingBag size={19} />
            </Link>
            <DonateButton className="button button--gold header-donate" label="Donate" />
            <button ref={menuButton} className="menu-button" type="button" onClick={() => setOpen(true)} aria-label="Open menu" aria-expanded={open}>
              <Menu />
            </button>
          </div>
        </div>
      </header>

      {open && <div className="mobile-drawer is-open">
        <button className="mobile-drawer__backdrop" onClick={() => setOpen(false)} aria-label="Close menu" />
        <aside className="mobile-drawer__panel" role="dialog" aria-modal="true" aria-label="Site menu" onKeyDown={trapDrawerFocus}>
          <div className="mobile-drawer__top">
            <Brand />
            <button ref={closeButton} className="icon-button" type="button" onClick={() => setOpen(false)} aria-label="Close menu">
              <X />
            </button>
          </div>
          <nav className="mobile-nav" aria-label="Mobile navigation">
            {navItems.map((item) => (
              <NavLink key={item.to} to={item.to} end={item.to === '/'}>
                {item.label}
              </NavLink>
            ))}
            <NavLink to="/shop">Shop</NavLink>
          </nav>
          <DonateButton className="button button--gold button--wide" />
        </aside>
      </div>}

      <main id="main-content" className="page-transition" key={location.pathname}>
        <Outlet />
      </main>

      <section className="footer-cta">
        <div className="container footer-cta__inner">
          <div>
            <span className="eyebrow eyebrow--light">Keep the promise going</span>
            <h2>Every horse here depends on daily care.</h2>
            <p>Your gift helps provide feed, medical attention, hoof care, safe shelter, and the patient work of healing.</p>
          </div>
          <DonateButton className="button button--cream button--large" label="Support the herd" />
        </div>
      </section>

      <footer className="site-footer">
        <div className="container site-footer__grid">
          <div className="site-footer__brand">
            <Brand />
            <div className="footer-donate">
              <span className="footer-donate__label">
                Help support their daily care
              </span>

              <DonateButton
                className="button button--gold footer-donate__button"
                label="Donate"
              />
            </div>
            <div className="social-row">
              <a href={socialLinks.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook"><Facebook /></a>
              <a href={socialLinks.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram"><Instagram /></a>
              <a href={socialLinks.tiktok} target="_blank" rel="noopener noreferrer" aria-label="TikTok"><span className="social-letter">T</span></a>
              <a href={socialLinks.youtube} target="_blank" rel="noopener noreferrer" aria-label="YouTube"><Youtube /></a>
            </div>
          </div>

          <div>
            <h3>Explore</h3>
            <div className="footer-links">
              {navItems.slice(0, 6).map((item) => <NavLink key={item.to} to={item.to}>{item.label}</NavLink>)}
              <NavLink to="/faqs">FAQs</NavLink>
              <NavLink to="/contact">Contact</NavLink>
              <NavLink to="/privacy">Privacy</NavLink>
              <button className="footer-settings" type="button" onClick={openPrivacySettings}>Privacy settings</button>
            </div>
          </div>

          <div>
            <h3>Contact</h3>
            <div className="footer-contact">
              <a href={`https://maps.google.com/?q=${encodeURIComponent(contact.address)}`} target="_blank" rel="noopener noreferrer"><MapPin />{contact.address}</a>
              <a href={`tel:${contact.phone.replace(/\D/g, '')}`}><Phone />{contact.phone}</a>
              <a href={`mailto:${contact.emails[0]}`}><Mail />{contact.emails[0]}</a>
              <a href={`mailto:${contact.emails[1]}`}><Mail />{contact.emails[1]}</a>
            </div>
          </div>
        </div>
        <div className="container site-footer__bottom">
          <span>Unique Diamond Horse Sanctuary is a qualified 501(c)(3) tax-exempt organization.</span>
          <span>© {new Date().getFullYear()} Unique Diamond Horse Sanctuary. All rights reserved.</span>
        </div>
      </footer>
      <PrivacyPreferences />
      <SiteUpdateNotice />
    </div>
  )
}
